// App.js
import { useState, useEffect } from 'react';
import Manzana from './Manzana';
import FormularioLote from './FormularioLote';
import plano from './plano.jpg';
import headLogo from './head.jpg'; // encabezado (fondo #002f31)
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
  Container,
  Box,
  Typography,
  CssBaseline,
  Grid,
  CircularProgress
} from '@mui/material';

// Tema MUI mejorado
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#00796B', // verde azulado moderno
    },
    secondary: {
      main: '#004D40',
    },
    background: {
      default: '#F5F7FA',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#212121',
      secondary: '#616161',
    },
  },
  typography: {
    fontFamily: "'Poppins', 'Roboto', sans-serif",
    button: { textTransform: 'none', fontWeight: 500 },
  },
  shape: {
    borderRadius: 12,
  },
});


// Definición de polígonos de manzanas
const shapes = [
  { id: 275, points: '100,85 490,80 490,500 100,450 100,85' },
  { id: 276, points: '590,85 1000,80 1065,590 590,530' },
  { id: 277, points: '1090,85 1830,85 1900,450 1170,590 1090,85' },
  { id: 278, points: '100,540 510,590 510,960 100,960' },
  { id: 279, points: '590,600 1090,660 1130,960 590,960 590,600' },
  { id: 280, points: '1160,690 1910,510 1970,960 1190,960 1160,690' }
];

const App = () => {
  const [manzanas, setManzanas] = useState([]);
  const [manzanaSeleccionada, setManzanaSeleccionada] = useState(null);
  const [loteSeleccionado, setLoteSeleccionado] = useState(null);
  const [expandPlano, setExpandPlano] = useState(false);
  const [modalManzana, setModalManzana] = useState(false);
  const [imagenManzana, setImagenManzana] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState(null);

  const moneda = 4;
  const etapa = 1;
  const currencySymbol = moneda === 2 ? 'U$S' : '$';
  const [emprendimiento, setEmprendimiento] = useState(7);

  // Tamaño real de la imagen para usar como viewBox
  const [vb, setVb] = useState({ w: 2048, h: 1484 });
  useEffect(() => {
    const img = new Image();
    img.src = plano;
    img.onload = () => setVb({ w: img.naturalWidth, h: img.naturalHeight });
  }, []);

  // Imagen de manzana
  const obtenerUrlImagenManzana = (id) => {
    const idStr = String(id).padStart(2, '0');
    return require(`./manzanas/M${idStr}.jpg`);
  };

  const abrirModalManzana = (id) => {
    const img = obtenerUrlImagenManzana(id);
    setImagenManzana(img);
    setModalManzana(true);
  };

  // Fetch corregido
  useEffect(() => {
    const url = `https://propietarios.pueblonuevo.com.ar/backendterrazas/lotestotales/${emprendimiento}/1?moneda=${moneda}&etapa=${etapa}`;

    setLoading(true);
    fetch(url)
      .then(res => (res.ok ? res.json() : Promise.reject('Error al cargar lotes')))
      .then(data => {
        const grouped = {};
        data.forEach(item => {
          if (!grouped[item.Manzana]) grouped[item.Manzana] = { idManzana: item.Manzana, lotes: [] };
          grouped[item.Manzana].lotes.push(item);
        });
        setManzanas(Object.values(grouped));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [moneda, emprendimiento]);

  const togglePlano = () => setExpandPlano(prev => !prev);

  if (loteSeleccionado) {
    return (
      <FormularioLote
        lote={loteSeleccionado}
        moneda={moneda}
        currencySymbol={currencySymbol}
        onCancelar={() => setLoteSeleccionado(null)}
        obtenerUrlImagenManzana={obtenerUrlImagenManzana}
      />
    );
  }

  const manzanasAMostrar = manzanaSeleccionada
    ? manzanas.filter(m => m.idManzana === manzanaSeleccionada)
    : manzanas;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container
        maxWidth="lg"
        disableGutters
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 0,
          px: 0,
        }}
      >
        {/* Encabezado responsive a todo ancho */}
        <Box
          component="header"
          sx={{
            width: '100%',
            height: 'auto',
            bgcolor: '#002f31',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            // padding vertical responsivo
            py: { xs: 0.2, sm: 2 },
            // sombra sutil para separar del plano
            boxShadow: '0 2px 12px rgba(0,0,0,0.25)'
          }}
        >
          <Box
            component="img"
            src={headLogo}
            alt="La Pampita — Loteo Viedma Sur"
            sx={{
              maxWidth: { xs: '88%', sm: 520, md: 640 },
              width: '20%',
              height: 'auto',
              display: 'block'
            }}
          />
        </Box>

        {/* Contenido principal */}
        <Box sx={{ width: '100%', px: { xs: 1.5, sm: 3 }, py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            // En móviles ocupa todo el ancho; en pantallas ≥sm limitamos por maxWidth
            maxWidth: expandPlano ? '100dvw' : { xs: '100%', sm: 600 },
            aspectRatio: `${vb.w} / ${vb.h}`, // mantiene proporción del plano
            transition: 'all 0.4s ease',
            mb: 4,
            cursor: 'pointer',
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            backdropFilter: 'blur(8px)',
            '&:hover': { transform: 'scale(1.02)' },
          }}
          onClick={togglePlano}
        >
          <svg
            viewBox={`0 0 ${vb.w} ${vb.h}`}
            preserveAspectRatio="xMidYMid meet"
            style={{
              width: '100%',
              height: '100%', // llena el contenedor con la misma relación de aspecto
              display: 'block',
              borderRadius: 8,
            }}
          >
            {/* Imagen base */}
            <image
              href={plano}
              x="0"
              y="0"
              width={vb.w}
              height={vb.h}
            />

            {/* Máscara */}
            <defs>
              <mask id="dimMask">
                <rect x="0" y="0" width={vb.w} height={vb.h} fill="white" />
                {manzanaSeleccionada && (
                  <polygon
                    points={shapes.find(p => p.id === manzanaSeleccionada)?.points}
                    fill="black"
                  />
                )}
              </mask>
            </defs>

            {/* Capa gris */}
            {manzanaSeleccionada && (
              <rect
                x="0"
                y="0"
                width={vb.w}
                height={vb.h}
                fill="rgba(0,0,0,0.4)"
                mask="url(#dimMask)"
                style={{ pointerEvents: 'none' }}
              />
            )}

            {/* Polígonos interactivos */}
            {shapes.map(s => {
              const isSelected = manzanaSeleccionada === s.id;
              const isHovered = hovered === s.id;
              return (
                <polygon
                  key={s.id}
                  points={s.points}
                  fill={
                    isSelected
                      ? 'rgba(76,175,80,0.2)'
                      : isHovered
                      ? 'rgba(76,175,80,0.1)'
                      : 'rgba(255,255,255,0.001)'
                  }
                  stroke={isSelected ? '#ffffff' : isHovered ? '#4caf50' : '#00470f'}
                  strokeWidth={isSelected ? 3 : isHovered ? 2 : 1}
                  vectorEffect="non-scaling-stroke"
                  style={{
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    filter: isHovered ? 'drop-shadow(0 0 5px #4caf50)' : 'none',
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    setManzanaSeleccionada(prev => (prev === s.id ? null : s.id));
                  }}
                  onMouseEnter={() => setHovered(s.id)}
                  onMouseLeave={() => setHovered(null)}
                />
              );
            })}
          </svg>
        </Box>

        {/* Grid de manzanas */}
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CircularProgress />
            <Typography mt={2}>Cargando lotes…</Typography>
          </Box>
        ) : (
          <Grid container spacing={3} justifyContent="center">
            {manzanasAMostrar.map((m, idx) => (
              <Grid item key={idx}>
                <Manzana
                  idManzana={m.idManzana}
                  lotes={m.lotes}
                  currencySymbol={currencySymbol}
                  onSeleccionarLote={l => setLoteSeleccionado(l)}
                  onAbrirModalManzana={abrirModalManzana}
                  obtenerUrlImagenManzana={obtenerUrlImagenManzana}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Modal de manzana */}
        {modalManzana && imagenManzana && (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              bgcolor: 'rgba(0,0,0,0.7)',
              backdropFilter: 'blur(6px)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              animation: 'fadeIn 0.3s ease-in-out',
            }}
            onClick={() => setModalManzana(false)}
          >
            <Box
              onClick={e => e.stopPropagation()}
              sx={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                borderRadius: 3,
                boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
                overflow: 'hidden',
                transform: 'scale(1)',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': { transform: 'scale(1.02)' },
              }}
            >
              <img src={imagenManzana} alt="Manzana" style={{ width: '100%', borderRadius: 8 }} />
            </Box>
          </Box>
        )}
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default App;
